/**
 * Date utility functions for the therapy management system
 */

/**
 * Converts a datetime string to date-only format for HTML date inputs
 * @param dateString - ISO datetime string (e.g., "2017-03-21T20:00:00.000Z")
 * @returns Date string in YYYY-MM-DD format (e.g., "2017-03-21")
 */
export const formatDateForInput = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  return dateString.split('T')[0]; // Extract YYYY-MM-DD part
};

/**
 * Converts a date to ISO string format for API submission
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns ISO datetime string
 */
export const formatDateForApi = (dateString: string): string => {
  if (!dateString) return '';
  return new Date(dateString + 'T00:00:00.000Z').toISOString();
};

/**
 * Gets today's date in YYYY-MM-DD format
 * @returns Today's date string
 */
export const getTodayDateString = (): string => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Formats a date for display in the UAE locale
 * @param dateString - ISO datetime string
 * @returns Formatted date string (e.g., "21 Mar 2017")
 */
export const formatDateForDisplay = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

/**
 * Calculates age from date of birth
 * @param dateOfBirth - Date of birth string
 * @returns Age in years
 */
export const calculateAge = (dateOfBirth: string): number => {
  if (!dateOfBirth) return 0;
  
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

/**
 * Checks if a date is overdue (past today)
 * @param dateString - Date string to check
 * @returns True if the date is in the past
 */
export const isOverdue = (dateString: string): boolean => {
  if (!dateString) return false;
  
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day
  
  return date < today;
};

/**
 * Gets the number of days between two dates
 * @param startDate - Start date string
 * @param endDate - End date string
 * @returns Number of days (positive if endDate is after startDate)
 */
export const getDaysDifference = (startDate: string, endDate: string): number => {
  if (!startDate || !endDate) return 0;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = end.getTime() - start.getTime();
  
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Formats a date range for display
 * @param startDate - Start date string
 * @param endDate - End date string
 * @returns Formatted date range string
 */
export const formatDateRange = (startDate: string, endDate: string): string => {
  if (!startDate || !endDate) return '';
  
  const start = formatDateForDisplay(startDate);
  const end = formatDateForDisplay(endDate);
  
  return `${start} - ${end}`;
};